"use client";

import { useEffect, useRef } from "react";

import Image from "next/image";

import { createPortal } from "react-dom";

type WaitlistSuccessModalProps = {
  onClose: () => void;
};

function CloseIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden>
      <path
        d="M2 2l10 10M12 2L2 12"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
      />
    </svg>
  );
}

export function WaitlistSuccessModal({ onClose }: WaitlistSuccessModalProps) {
  const doneButtonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    doneButtonRef.current?.focus();

    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === "Escape") {
        onClose();
      }
    }

    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [onClose]);

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" role="presentation">
      <button
        type="button"
        className="absolute inset-0 bg-black/60 backdrop-brightness-50"
        aria-label="Close dialog"
        onClick={onClose}
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="waitlist-success-title"
        aria-describedby="waitlist-success-description"
        className="relative w-full max-w-88">
        <button
          type="button"
          onClick={onClose}
          className="text-foreground absolute -top-12 -right-1 z-10 flex size-9 items-center justify-center rounded-full border border-white/20 bg-[#1f1f1f] transition-colors hover:bg-[#2a2a2a]"
          aria-label="Close">
          <CloseIcon />
        </button>
        <div className="rounded-2xl bg-[#1f1f1f] px-6 pt-8 pb-6 text-center shadow-xl">
          <Image
            src="/done-tick.svg"
            alt=""
            width={94}
            height={94}
            className="mx-auto mb-6"
            aria-hidden
          />
          <h2
            id="waitlist-success-title"
            className="text-foreground text-xl font-bold tracking-tight sm:text-[1.375rem]">
            You&apos;re officially in!
          </h2>
          <p
            id="waitlist-success-description"
            className="mt-2 text-sm leading-relaxed text-gray-400 sm:text-[0.9375rem]">
            We'll let you know the moment NOMISS! launches.
          </p>
          <button
            ref={doneButtonRef}
            type="button"
            onClick={onClose}
            className="bg-brand-lime mt-6 w-full rounded-2xl py-3.5 text-base font-semibold text-[#0f0f0f] transition-opacity hover:opacity-90">
            Done
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
